import React, { FC, useEffect, useState } from 'react';
import { Button, Input } from '../../components';
import OtpInput from '../../components/otpInput/otpInput.index';
import { RiEdit2Line } from 'react-icons/ri';
import { LoadingOutlined } from '@ant-design/icons';
import { sendCode, signInVerify2Fa, verifyForgetPassword, verifyPwOrCode } from 'services';
import { faNumToEnNum } from '../../../scripts';

interface Props {
    stage?: 'register' | '2fa' | 'forgot' | 'once';
    phoneNumber?: string;
    isRegister?: boolean;
    hasPassword?: boolean;
    isTwoFactorEnabled?: boolean;
    onEditPhoneNumberClick: () => void;
    onRegisterComplete: (isTwoFactorEnabled?: boolean) => void;
    onLoginWithCodeComplete: (isTwoFactorEnabled: boolean) => void;
    onForgotPasswordComplete: (accessToken: string) => void;
    onSignInVerify2FaComplete: () => void;
}

export const EnterCode: FC<Props> = (props) => {
    const [code, setCode] = useState<string>();
    const [referral, setReferral] = useState<string>('');
    const [remainingTime, setRemainingTime] = useState<number>(120);
    const [isTimerActive, setIsTimerActive] = useState<boolean>(true);
    const [resendCodeLoading, setResendCodeLoading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [onMountLoading, setOnMountLoading] = useState<boolean>(false);

    useEffect(() => {
        if (isTimerActive && remainingTime > 0) {
            const timer = setInterval(() => {
                setRemainingTime((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        } else if (remainingTime === 0) {
            setIsTimerActive(false);
        }
    }, [isTimerActive, remainingTime]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const refValue = params.get('ref');

        if (refValue) {
            setReferral(refValue);
        }
    }, []);

    const onMount = () => {
        if (props.stage === 'once' && props.hasPassword) {
            const onComplete = () => setRemainingTime(120);
            sendCode(setOnMountLoading, { phoneNumber: props.phoneNumber!, forPasswordCheck: false }, onComplete);
        }
    };

    useEffect(onMount, []);

    const handleResendClick = () => {
        const onComplete = () => {
            setCode(undefined);
            setRemainingTime(120);
            setIsTimerActive(true);
        };

        sendCode(setResendCodeLoading, { phoneNumber: props.phoneNumber!, forPasswordCheck: false }, onComplete);
    };

    const handleSubmit = () => {
        if (!props.isTwoFactorEnabled) {
            const data = {
                phoneNumber: props.phoneNumber,
                code: code,
                ...(props.isRegister ? {} : { referral }),
            };

            if (props.stage === 'once')
                verifyPwOrCode(setLoading, data, (isTwoFactorEnabled, accessToken) => {
                    if (props.hasPassword) {
                        props.onLoginWithCodeComplete(isTwoFactorEnabled);
                    } else if (accessToken) {
                        props.onForgotPasswordComplete(accessToken);
                    } else {
                        props.onLoginWithCodeComplete(isTwoFactorEnabled);
                    }
                });

            if (props.stage === 'forgot') verifyForgetPassword(setLoading, data, props.onForgotPasswordComplete);

            if (props.stage === 'register') verifyPwOrCode(setLoading, data, props.onRegisterComplete);
        } else {
            const data = {
                phoneNumber: props.phoneNumber,
                code: code,
            };

            if (props.stage === 'once')
                verifyPwOrCode(setLoading, data, (isTwoFactorEnabled, accessToken) => {
                    if (props.hasPassword) {
                        props.onLoginWithCodeComplete(isTwoFactorEnabled);
                    } else if (accessToken) {
                        props.onForgotPasswordComplete(accessToken);
                    }
                });

            if (props.stage === '2fa') signInVerify2Fa(setLoading, data, props.onSignInVerify2FaComplete);
        }
    };

    useEffect(() => {
        if (code?.length === 6) {
            handleSubmit();
        }
    }, [code]);

    return (
        <div className={'flex flex-col w-full'}>
            {props.stage === '2fa' ? (
                <p className={'text-base text-darkPurple-100 mt-6 font-light max-lg:text-sm'}>
                    کد دوعاملی گوگل را وارد کنید.
                </p>
            ) : (
                <p className={'text-base text-darkPurple-100 mt-6 font-light max-lg:text-sm'}>
                    کد پیامک شده به شماره {props.phoneNumber} را وارد کنید.
                </p>
            )}

            {props.stage === '2fa' ? (
                <label className={'text-base mt-4 font-light max-lg:text-sm'}>کد ۶ رقمی گوگل:</label>
            ) : (
                <label className={'text-base mt-4 font-light max-lg:text-sm'}>کد پیامک شده:</label>
            )}

            <OtpInput length={6} onChange={(otp) => setCode(faNumToEnNum(otp))} />
            <div className={'flex justify-between mt-4 text-sm max-lg:text-xs'}>
                {props.stage !== '2fa' &&
                    !onMountLoading &&
                    (remainingTime === 0 ? (
                        <p
                            className={`flex items-center gap-2 font-bold clickable ${resendCodeLoading ? '!opacity-50' : ''}`}
                            onClick={handleResendClick}
                        >
                            ارسال مجدد کد
                            {resendCodeLoading ? <LoadingOutlined /> : null}
                        </p>
                    ) : (
                        <p className={'font-medium'}>
                            <span className={'font-bold'}>{remainingTime} ثانیه </span>تا ارسال مجدد کد
                        </p>
                    ))}
                <p
                    className={'flex items-center gap-1 text-primary-80 clickable'}
                    onClick={props.onEditPhoneNumberClick}
                >
                    ویرایش شماره موبایل
                    <RiEdit2Line />
                </p>
            </div>
            {!props.isRegister && (
                <>
                    <label className={'text-base mt-4 font-light'}>کد معرف (اختیاری):</label>
                    <Input
                        className={'mt-2'}
                        placeholder={'کد معرف'}
                        value={referral}
                        dir={referral ? 'ltr' : 'rtl'}
                        onChange={(e) => setReferral(e.target.value)}
                    />
                </>
            )}
            <Button
                className={'mt-6'}
                type={'primary'}
                onClick={handleSubmit}
                loading={loading}
                disabled={resendCodeLoading || loading || code?.length !== 6 || onMountLoading}
            >
                تایید کد
            </Button>
        </div>
    );
};
